import { FC, useCallback, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslations } from '@altermaind/core/i18n';
import { Box, Typography, Svg, Button, useThemeValue } from '@altermaind/web/ui';
import { Routes } from '@/_shared/constants/routes';
import { convertSizeToNumber } from '@/_shared/helpers';
import { useDialog, useNotification } from '@/_shared/hooks';
import { useWizard } from '@/_shared/hooks/useWizard';
import { getEmployerCreditsCall } from '@/credits/state/api/getEmployerCreditsCall';
import { getContentDetailsCall } from '@/training/hardSkills/state/api';
import { postQuizStartCall } from '../../state/api';
import { DialogConfirmRestartQuizPayload } from './types';

export const DialogConfirmRestartQuiz: FC = () => {
  const t = useTranslations('quiz.confirmRestartQuizDialog');
  const tShared = useTranslations();
  const { notify } = useNotification();
  const navigate = useNavigate();
  const { closeDialog, Dialog, isOpen, payload } = useDialog<DialogConfirmRestartQuizPayload>(
    'confirmRestartQuizDialog',
  );
  const gapL = convertSizeToNumber(`${useThemeValue('sizes', 'l')}`);
  const { execute: executeGetEmployerCredits } = getEmployerCreditsCall.hook();
  const { response: responseGetContentDetails } = getContentDetailsCall.hook();
  const { execute: executePostQuizStart, loading: loadingPostQuizStart } = postQuizStartCall.hook();
  const { handlePrevious } = useWizard();

  const resetWizardAtFirstStepAndRedirectToNewSubmission = useCallback(
    (quizId: string, id: string) => {
      const FIRST_WIZARD_STEP_INDEX = 0;
      payload?.navigate?.(FIRST_WIZARD_STEP_INDEX);
      navigate(Routes.Quiz.replace(':quizId', quizId).replace(':submissionId', id), {
        replace: true,
      });
      window.scrollTo({ top: 0, behavior: 'smooth' });
    },
    [navigate, payload],
  );

  const onRestartConfirm = useCallback(() => {
    if (responseGetContentDetails?.id) {
      executePostQuizStart({
        quizId: responseGetContentDetails.id || '',
        successSaga: ({ response }) => {
          if (responseGetContentDetails.credits) {
            executeGetEmployerCredits({});
          }
          if (response?.id) {
            resetWizardAtFirstStepAndRedirectToNewSubmission(
              responseGetContentDetails.id,
              response.id,
            );
          }
        },
        errorSaga: () => {
          notify(tShared('errors.genericApiError'), 'error');
        },
      });
    }
  }, [
    executeGetEmployerCredits,
    executePostQuizStart,
    notify,
    resetWizardAtFirstStepAndRedirectToNewSubmission,
    responseGetContentDetails?.credits,
    responseGetContentDetails?.id,
    tShared,
  ]);

  useEffect(() => {
    if (isOpen && !responseGetContentDetails?.credits) {
      const quizId = responseGetContentDetails?.id || '';
      executePostQuizStart({
        quizId,
        successSaga: ({ response }) => {
          if (response?.id) {
            resetWizardAtFirstStepAndRedirectToNewSubmission(quizId, response.id);
          }
        },
        errorSaga: () => {
          notify(tShared('errors.genericApiError'), 'error');
        },
      });
    }
  }, [
    closeDialog,
    executePostQuizStart,
    handlePrevious,
    isOpen,
    navigate,
    notify,
    payload,
    resetWizardAtFirstStepAndRedirectToNewSubmission,
    responseGetContentDetails,
    tShared,
  ]);

  if (isOpen && !responseGetContentDetails?.credits) {
    return null;
  }

  return (
    <Dialog maxWidth='700px' open={isOpen} title={undefined} onClose={closeDialog} closeButton>
      <Box display='flex' mx='l' flexDirection='column' px='s'>
        <Typography variant='h3' color='primary/text' textAlign='center'>
          {t('title')}
        </Typography>
      </Box>
      <Box display='flex' mt='s' flexDirection='column' px='s'>
        <Typography variant='p2/light' color='primary/text' textAlign='center'>
          {t('description', { credits: responseGetContentDetails?.credits || 0 })}
        </Typography>
      </Box>

      <Box
        display='flex'
        mx='l'
        flexDirection='row'
        px='s'
        style={{ gap: gapL }}
        justifyContent='center'
      >
        <Button
          variant='link/primary'
          onClick={closeDialog}
          mt='l'
          style={{ textDecoration: 'none' }}
        >
          <Svg name='close' fill='primary/text' size='s' mr='xs' />
          {t('cancel')}
        </Button>

        <Button
          variant='primary'
          onClick={onRestartConfirm}
          mt='l'
          loading={loadingPostQuizStart}
          spinnerColor='white'
        >
          {t('confirm')}
        </Button>
      </Box>
    </Dialog>
  );
};
